import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import { type IProductionStatement } from "@/interfaces/api/production-statement.interface"
import { DraftProductionStatement } from "@/models/draft-production-statement.model"
import { ProductionStatement } from "@/models/production-statement.model"
import { ProductionStatementCreationJob } from "@/models/production-statement-creation-job.model"
import { ProductionStatementRepository } from "@/repositories/production-statement-repository"
import { useProductionStatementStore } from "@/store/production-statements.store"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { storeToRefs } from "pinia"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import type { Moment } from "moment"
import { ProductionStatementCreationJobController } from "@/controllers/model-controllers/production-statement-creation-job-controller"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"

export class ProductionStatementController extends GenericModelController<
  ProductionStatement,
  IProductionStatement,
  ProductionStatementRepository
> {
  private productionStatementCreationJobController: ProductionStatementCreationJobController

  constructor() {
    const productionStatementStore = useProductionStatementStore()
    const { productionStatements } = storeToRefs(productionStatementStore)
    const storeFunctionsConfig = {
      collection: productionStatements
    }

    super(ProductionStatement, storeFunctionsConfig, new ProductionStatementRepository())

    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      ProductionStatement,
      productionStatements
    )
    this.productionStatementCreationJobController = new ProductionStatementCreationJobController()
  }

  public async retrieveProductionStatementCreationJobs(): Promise<
    ProductionStatementCreationJob[]
  > {
    return await this.productionStatementCreationJobController.getList()
  }

  public async restartProductionStatementCreationBatch(
    batchId: string
  ): Promise<ProductionStatementCreationJob[]> {
    return await this.productionStatementCreationJobController.restartBatch(batchId)
  }

  public async rollbackProductionStatementCreationJob(
    job: ProductionStatementCreationJob
  ): Promise<void> {
    await this.productionStatementCreationJobController.rollback(job)
  }

  public async retrieveDraftProductionStatements(
    query?: ODataQueryBuilder
  ): Promise<DraftProductionStatement[]> {
    const drafts = await this.apiFunctions.getRepository().getDraftProductionStatements(query)
    return drafts.map((draft) => new DraftProductionStatement(draft))
  }

  public async bulkGenerateProductionStatements(
    query?: ODataQueryBuilder
  ): Promise<ProductionStatementCreationJob[]> {
    const jobs = await this.apiFunctions.getRepository().bulkGenerateProductionStatements(query)
    // Our own socket messages are ignored, so add callback jobs immediately rather than waiting for a socket event.
    return await this.productionStatementCreationJobController.addJobsToStore(jobs)
  }

  public async generateDraftProductionStatements(
    drafts: DraftProductionStatement[],
    dateRange: [Moment, Moment] | null = null
  ): Promise<ProductionStatementCreationJob[]> {
    // One predicate per selected project plus the list's shared period filter. The period scopes which rows are
    // reserved; each job still narrows its own period to the actual span of the rows it reserves.
    const query = new ODataQueryBuilder().or("selectedProjects", (selectedProjectsQuery) => {
      for (const draft of drafts) {
        selectedProjectsQuery.eq("project", draft.project.id, draft.draftKey)
      }
    })
    if (dateRange) {
      query
        .ge("period_start", dateRange[0].format("YYYY-MM-DD"))
        .le("period_end", dateRange[1].format("YYYY-MM-DD"))
    }

    return await this.bulkGenerateProductionStatements(query)
  }

  public async rollbackProductionStatements(
    productionStatements: ProductionStatement[]
  ): Promise<ProductionStatement[]> {
    const isRolledBack = await this.bulkDelete(productionStatements)
    return isRolledBack ? productionStatements : []
  }

  public async downloadProductionStatements(
    productionStatements: ProductionStatement[]
  ): Promise<Blob> {
    return await this.apiFunctions
      .getRepository()
      .downloadProductionStatements(
        productionStatements.map((productionStatement) => productionStatement.id!)
      )
  }

  public async sendProductionStatement(
    productionStatement: ProductionStatement
  ): Promise<ProductionStatement> {
    const updatedProductionStatement = await this.apiFunctions
      .getRepository()
      .sendProductionStatement(productionStatement)
    return this.storeFunctions.updateModel(updatedProductionStatement)
  }

  public async dispute(
    productionStatement: ProductionStatement,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<ProductionStatement | undefined> {
    return await this.update(productionStatement, {
      ...requestConfig,
      query: { dispute: true }
    })
  }

  public async removeDispute(
    productionStatement: ProductionStatement,
    requestConfig?: IRepositoryRequestConfig
  ): Promise<ProductionStatement | undefined> {
    return await this.update(productionStatement, {
      ...requestConfig,
      query: { undispute: true }
    })
  }

  /**
   * Refreshes one draft from the drafts endpoint. Drafts have no server-side id,
   * so the project and the draft's period form its stable client-side identity.
   */
  public async refreshDraftProductionStatement(
    draft: DraftProductionStatement,
    dateRange: [Moment, Moment] | null
  ): Promise<DraftProductionStatement | undefined> {
    const query = new ODataQueryBuilder().eq("project", draft.project.id)
    if (dateRange) {
      query
        .ge("period_start", dateRange[0].format("YYYY-MM-DD"))
        .le("period_end", dateRange[1].format("YYYY-MM-DD"))
    }

    const drafts = await this.retrieveDraftProductionStatements(query)
    return drafts.find((candidate) => candidate.draftKey === draft.draftKey)
  }
}
